import { useEffect, useState } from "react";

/**
 * Three concentric semi-circular arcs — speedometer style.
 * Outer → inner: Easy (teal) · Medium (amber) · Hard (red)
 */
export default function ProgressMeter({ easy, medium, hard, connected = true }) {
  const cx = 110, cy = 112;

  // Safely normalise each prop — works even if undefined or missing solved/total
  const norm = (d) => ({ solved: d?.solved ?? 0, total: d?.total ?? 0 });
  const e = norm(easy), m = norm(medium), h = norm(hard);

  const rings = [
    { label: "Easy",   color: "#2dd4bf", glow: "rgba(45,212,191,0.35)",  track: "rgba(45,212,191,0.18)",  r: 84, sw: 9, ...e },
    { label: "Medium", color: "#fbbf24", glow: "rgba(251,191,36,0.35)",   track: "rgba(251,191,36,0.18)",   r: 65, sw: 9, ...m },
    { label: "Hard",   color: "#f87171", glow: "rgba(248,113,113,0.35)", track: "rgba(248,113,113,0.18)", r: 46, sw: 9, ...h },
  ];

  const noData = !connected || (e.total === 0 && m.total === 0 && h.total === 0);

  // Animate after first paint
  const [progress, setProgress] = useState(rings.map(() => 0));
  useEffect(() => {
    const t = setTimeout(() => {
      setProgress(
        rings.map(({ solved, total }) =>
          total > 0 ? Math.min((solved ?? 0) / total, 1) : 0
        )
      );
    }, 120);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [easy, medium, hard]);

  // Top semi-circle: clockwise (sweep=1) from left → top → right
  const arcPath = (r) => `M ${cx - r},${cy} A ${r} ${r} 0 0 1 ${cx + r},${cy}`;

  const totalSolved   = e.solved + m.solved + h.solved;
  const totalProblems = e.total  + m.total  + h.total;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
      <svg viewBox="0 0 220 126" style={{ width: "100%", maxWidth: 280, overflow: "visible" }}>
        <defs>
          {rings.map(({ r, color, glow }) => (
            <filter key={r} id={`glow-${r}`} x="-40%" y="-40%" width="180%" height="180%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feFlood floodColor={glow} result="color" />
              <feComposite in="color" in2="blur" operator="in" result="glow" />
              <feMerge><feMergeNode in="glow" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          ))}
        </defs>

        {rings.map(({ r, sw, color, track, glow }, i) => {
          const circ = Math.PI * r;
          const filled = progress[i] * circ;
          const offset = circ - filled;
          return (
            <g key={r}>
              {/* track */}
              <path
                d={arcPath(r)}
                fill="none"
                stroke={track}
                strokeWidth={sw}
                strokeLinecap="round"
              />
              {/* filled arc */}
              <path
                d={arcPath(r)}
                fill="none"
                stroke={color}
                strokeWidth={sw}
                strokeLinecap="round"
                strokeDasharray={circ}
                strokeDashoffset={offset}
                filter={progress[i] > 0 ? `url(#glow-${r})` : undefined}
                style={{
                  transition: "stroke-dashoffset 1.2s cubic-bezier(0.34,1.0,0.64,1)",
                }}
              />
            </g>
          );
        })}

        {/* Centre text */}
        {noData ? (
          <text x={cx} y={cy - 8} textAnchor="middle" fontSize={11.5}
            fontFamily="var(--font-display)" fill="rgba(148,163,184,0.45)" letterSpacing="0.01em">
            {connected ? "Sync to load progress" : "Connect LeetCode first"}
          </text>
        ) : (<>
        <text
          x={cx} y={cy - 18}
          textAnchor="middle"
          fontSize={30}
          fontWeight={700}
          fontFamily="var(--font-display)"
          fill="#e2e8f0"
          letterSpacing="-0.03em"
        >
          {totalSolved.toLocaleString()}
        </text>
        <text
          x={cx} y={cy - 2}
          textAnchor="middle"
          fontSize={10}
          fontFamily="var(--font-display)"
          fill="rgba(148,163,184,0.45)"
          letterSpacing="0.04em"
        >
          / {totalProblems.toLocaleString()} SOLVED
        </text>
        </>)}
      </svg>

      {/* Legend */}
      <div style={{ display: "flex", gap: 20, justifyContent: "center" }}>
        {rings.map(({ label, color, solved, total }) => (
          <div key={label} style={{ textAlign: "center", minWidth: 56 }}>
            <div style={{
              fontSize: 16,
              fontWeight: 700,
              fontFamily: "var(--font-display)",
              color,
              letterSpacing: "-0.02em",
            }}>
              {(solved ?? 0).toLocaleString()}
            </div>
            <div style={{
              fontSize: 10.5,
              color: "rgba(148,163,184,0.5)",
              fontFamily: "var(--font-display)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginTop: 1,
            }}>
              {label}
            </div>
            <div style={{
              fontSize: 10,
              color: "rgba(148,163,184,0.25)",
              fontFamily: "var(--font-display)",
              marginTop: 1,
            }}>
              / {(total ?? 0).toLocaleString()}
            </div>
          </div>
        ))}
      </div>

      <div style={{
        fontSize: 10.5,
        color: "rgba(148,163,184,0.28)",
        fontFamily: "var(--font-display)",
        textAlign: "center",
        lineHeight: 1.5,
        maxWidth: 220,
      }}>
        Contest problems only — not your full LeetCode history
      </div>
    </div>
  );
}

